import { notificationsApi } from "@/api/http";

export interface InboxNotification {
  id: number;
  eventType: string;
  title: string;
  body: string;
  referenceId: number | null;
  referenceType: string | null;
  read: boolean;
  createdAt: string;
}

/** Envelope returned by the paged inbox endpoint. */
export interface InboxNotificationPage {
  content: InboxNotification[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

const basePath = (businessId: string) =>
  `/api/v1/businesses/${businessId}/notifications`;

export const notificationsInboxApi = {
  list: async (businessId: string): Promise<InboxNotification[]> => {
    const { data } = await notificationsApi.get<InboxNotification[]>(basePath(businessId));
    return Array.isArray(data) ? data : [];
  },

  /**
   * Paged inbox. Prefer this over `list` — the inbox accumulates for the life of the
   * business and is never pruned, so an unpaged fetch grows without bound.
   */
  listPaged: async (
    businessId: string,
    page: number,
    size: number,
  ): Promise<InboxNotificationPage> => {
    const { data } = await notificationsApi.get<unknown>(`${basePath(businessId)}/page`, {
      params: { page, size },
    });
    const body = (data ?? {}) as Record<string, unknown>;
    const content = Array.isArray(body.content) ? (body.content as InboxNotification[]) : [];
    return {
      content,
      page: Number(body.page ?? 0),
      size: Number(body.size ?? size),
      totalElements: Number(body.totalElements ?? content.length),
      totalPages: Number(body.totalPages ?? 1),
    };
  },

  unreadCount: async (businessId: string): Promise<number> => {
    const { data } = await notificationsApi.get<{ count: number }>(
      `${basePath(businessId)}/unread-count`,
    );
    return data?.count ?? 0;
  },

  markRead: async (businessId: string, notificationId: number): Promise<void> => {
    await notificationsApi.patch(`${basePath(businessId)}/${notificationId}/read`);
  },

  markAllRead: async (businessId: string): Promise<void> => {
    await notificationsApi.patch(`${basePath(businessId)}/read-all`);
  },
};
